import { Component, Input } from '@angular/core';
import { semhColour, semhLabel } from '../models/leap-v2-semh';

/**
 * A previous → current comparison, read-only.
 *
 * Its own component rather than an input on leap-v2-scale-slider, because the QA review table has no
 * sliders at all — a comparator living inside one would be unreachable from the place it is most
 * needed. The slider is also an input control (onInput/valueChange) bound to scales that have no
 * "previous" concept at all (key stage, percent ticks), so widening it would leave every one of those
 * bindings carrying a latent undefined.
 *
 * 'label' renders the SEMH pair ("Low ▲ Moderate", the current value coloured from the shared ramp);
 * 'percent' renders the academic pair ("38% → 49%") with a signed delta.
 */
@Component({
  selector: 'leap-v2-delta',
  templateUrl: './leap-v2-delta.component.html',
  styleUrls: ['./leap-v2-delta.component.css'],
  standalone: false,
})
export class LeapV2DeltaComponent {
  @Input() previous: number | null = null;
  @Input() current: number | null = null;
  @Input() display: 'label' | 'percent' = 'label';

  get hasPrevious(): boolean {
    return this.previous != null;
  }

  get hasCurrent(): boolean {
    return this.current != null;
  }

  /** 'same' whenever either end is missing — an unknown move is not an unchanged one. */
  get direction(): 'up' | 'down' | 'same' {
    if (!this.hasPrevious || !this.hasCurrent || this.previous === this.current) {
      return 'same';
    }
    return this.current > this.previous ? 'up' : 'down';
  }

  get arrow(): string {
    if (this.direction === 'up') {
      return '▲';
    }
    return this.direction === 'down' ? '▼' : '–';
  }

  /**
   * An unset previous reads '—', never a label: clampSemh lifts null to 1, so labelling it would
   * claim the learner was rated "Very low" when they were never rated at all.
   */
  get previousText(): string {
    if (!this.hasPrevious) {
      return '—';
    }
    return this.display === 'percent' ? this.previous + '%' : semhLabel(this.previous);
  }

  get currentText(): string {
    if (!this.hasCurrent) {
      return '—';
    }
    return this.display === 'percent' ? this.current + '%' : semhLabel(this.current);
  }

  /** The current value's colour on the shared red→green ramp; percent mode is not colour-coded. */
  get currentColour(): string | null {
    if (this.display === 'percent' || !this.hasCurrent) {
      return null;
    }
    return semhColour(this.current);
  }

  /** Signed movement, percent mode only. Null when either end is missing. */
  get delta(): number | null {
    if (!this.hasPrevious || !this.hasCurrent) {
      return null;
    }
    return this.current - this.previous;
  }

  get deltaText(): string {
    const delta = this.delta;
    return delta > 0 ? '+' + delta : String(delta);
  }
}
